import React, { useEffect, useMemo, useState } from 'react';
import { Card, Button, Spinner, Alert, TabbedComponent } from '@dopomogai/ui';
import type { UUID, McpServerItem } from '@dopomogai/types';
import { useMcpServers } from '@dopomogai/agent-kit';
import McpServerForm from './McpServerForm';
import { Plus, Network, Radio, Terminal, Save } from 'lucide-react';

type McpServersConfig = {
  http?: McpServerItem[];
  sse?: McpServerItem[];
  stdio?: McpServerItem[];
};

export interface McpServersCardProps {
  agentId: UUID;
  value: McpServersConfig;
  onChange: (next: McpServersConfig) => void;
  onSave?: () => Promise<void> | void;
  isSaving?: boolean;
}

const emptyConfig: McpServersConfig = { http: [], sse: [], stdio: [] };

const getNextId = (items: McpServerItem[] | undefined, type: 'http' | 'sse' | 'stdio') => {
  const ids = (items || []).map(i => i.server_id);
  let idx = 0;
  while (ids.includes(`${type}:${idx}`)) idx += 1;
  return `${type}:${idx}`;
};

export const McpServersCard: React.FC<McpServersCardProps> = ({
  agentId, value, onChange, onSave, isSaving
}) => {
  const [local, setLocal] = useState<McpServersConfig>(emptyConfig);
  const { servers: runtimeServers, isLoading, error, test } = useMcpServers(agentId);
  const [activeTab, setActiveTab] = useState<string>('http');
  const [testing, setTesting] = useState<Record<string, { ok?: boolean; status?: number | null; error?: string | null }>>({});

  useEffect(() => {
    setLocal({
      http: (value.http || []).map(v => ({ ...v })),
      sse: (value.sse || []).map(v => ({ ...v })),
      stdio: (value.stdio || []).map(v => ({ ...v })),
    });
  }, [value]);

  const setList = (type: 'http' | 'sse' | 'stdio', items: McpServerItem[]) => {
    setLocal(prev => ({ ...(prev || emptyConfig), [type]: items }));
  };

  const addServer = (type: 'http' | 'sse' | 'stdio') => {
    const list = [...(local[type] || [])];
    const server: McpServerItem = {
      server_id: getNextId(local[type], type),
      type,
      url: type !== 'stdio' ? '' : undefined,
      command: type === 'stdio' ? '' : undefined,
      args: type === 'stdio' ? [] : undefined,
      headers_template: {},
      allow_sampling: false,
      verify_tls: type !== 'stdio' ? true : undefined,
      timeout_sec: 10,
      tool_prefix: '',
    };
    list.push(server);
    setList(type, list);
  };

  const updateServer = (type: 'http' | 'sse' | 'stdio', idx: number, server: McpServerItem) => {
    const list = [...(local[type] || [])];
    list[idx] = server;
    setList(type, list);
  };

  const removeServer = (type: 'http' | 'sse' | 'stdio', idx: number) => {
    const list = [...(local[type] || [])];
    list.splice(idx, 1);
    setList(type, list);
  };

  const apply = () => {
    onChange(local);
  };

  const doTest = async (serverId: string) => {
    setTesting(prev => ({ ...prev, [serverId]: { ok: undefined, status: undefined, error: undefined } }));
    try {
      const res = await test(serverId);
      setTesting(prev => ({ ...prev, [serverId]: { ok: res.ok, status: res.status ?? null, error: res.error || null } }));
    } catch (e: any) {
      setTesting(prev => ({ ...prev, [serverId]: { ok: false, status: null, error: e?.message || 'Test failed' } }));
    }
  };

  const httpTab = (
    <div className="space-y-3">
      {(local.http || []).map((s, idx) => (
        <Card key={s.server_id} noPadding={false} className="border border-dashed bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Network className="w-4 h-4 text-slate-700" />
              <div className="text-sm font-semibold text-slate-900">{s.server_id}</div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" onClick={() => doTest(s.server_id)} disabled={isLoading}>
                Test
              </Button>
              <Button size="sm" variant="ghost" onClick={() => removeServer('http', idx)}>
                Remove
              </Button>
            </div>
          </div>
          <div className="mt-3">
            <McpServerForm server={s} onChange={(next) => updateServer('http', idx, next)} />
            {testing[s.server_id] && (
              <div className="text-xs text-slate-600 mt-2">
                {testing[s.server_id].ok === undefined ? null : testing[s.server_id].ok ? (
                  <span className="text-emerald-600">OK</span>
                ) : (
                  <span className="text-rose-600">Error: {testing[s.server_id].error || `HTTP ${testing[s.server_id].status ?? ''}`}</span>
                )}
              </div>
            )}
          </div>
        </Card>
      ))}
      <Button variant="secondary" size="sm" onClick={() => addServer('http')} icon={<Plus className="w-4 h-4" />}>
        Add HTTP server
      </Button>
    </div>
  );

  const sseTab = (
    <div className="space-y-3">
      {(local.sse || []).map((s, idx) => (
        <Card key={s.server_id} noPadding={false} className="border border-dashed bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Radio className="w-4 h-4 text-slate-700" />
              <div className="text-sm font-semibold text-slate-900">{s.server_id}</div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" onClick={() => doTest(s.server_id)} disabled={isLoading}>
                Test
              </Button>
              <Button size="sm" variant="ghost" onClick={() => removeServer('sse', idx)}>
                Remove
              </Button>
            </div>
          </div>
          <div className="mt-3">
            <McpServerForm server={s} onChange={(next) => updateServer('sse', idx, next)} />
            {testing[s.server_id] && (
              <div className="text-xs text-slate-600 mt-2">
                {testing[s.server_id].ok === undefined ? null : testing[s.server_id].ok ? (
                  <span className="text-emerald-600">OK</span>
                ) : (
                  <span className="text-rose-600">Error: {testing[s.server_id].error || `HTTP ${testing[s.server_id].status ?? ''}`}</span>
                )}
              </div>
            )}
          </div>
        </Card>
      ))}
      <Button variant="secondary" size="sm" onClick={() => addServer('sse')} icon={<Plus className="w-4 h-4" />}>
        Add SSE server
      </Button>
    </div>
  );

  const stdioTab = (
    <div className="space-y-3">
      {(local.stdio || []).map((s, idx) => (
        <Card key={s.server_id} noPadding={false} className="border border-dashed bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Terminal className="w-4 h-4 text-slate-700" />
              <div className="text-sm font-semibold text-slate-900">{s.server_id}</div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" onClick={() => doTest(s.server_id)} disabled={isLoading}>
                Test
              </Button>
              <Button size="sm" variant="ghost" onClick={() => removeServer('stdio', idx)}>
                Remove
              </Button>
            </div>
          </div>
          <div className="mt-3">
            <McpServerForm server={s} onChange={(next) => updateServer('stdio', idx, next)} />
            {testing[s.server_id] && (
              <div className="text-xs text-slate-600 mt-2">
                {testing[s.server_id].ok === undefined ? null : testing[s.server_id].ok ? (
                  <span className="text-emerald-600">OK</span>
                ) : (
                  <span className="text-rose-600">Error: {testing[s.server_id].error || `HTTP ${testing[s.server_id].status ?? ''}`}</span>
                )}
              </div>
            )}
          </div>
        </Card>
      ))}
      <Button variant="secondary" size="sm" onClick={() => addServer('stdio')} icon={<Plus className="w-4 h-4" />}>
        Add STDIO server
      </Button>
    </div>
  );

  const tabs = [
    { id: 'http', label: 'HTTP', icon: <Network className="w-4 h-4" />, content: httpTab },
    { id: 'sse', label: 'SSE', icon: <Radio className="w-4 h-4" />, content: sseTab },
    { id: 'stdio', label: 'STDIO', icon: <Terminal className="w-4 h-4" />, content: stdioTab },
  ];

  const dirty = useMemo(() => JSON.stringify(local || emptyConfig) !== JSON.stringify(value || emptyConfig), [local, value]);

  return (
    <Card title="External toolsets (MCP)" className="border-2 border-black" headerBgColor="bg-slate-100" noPadding>
      {error ? (
        <div className="p-4">
          <Alert type="warning" title="Runtime servers unavailable" message={error.message} />
        </div>
      ) : isLoading ? (
        <div className="p-4">
          <Spinner text="Loading runtime servers…" />
        </div>
      ) : null}

      <div className="p-3">
        <TabbedComponent tabs={tabs} activeTabId={activeTab} onTabChange={setActiveTab} />
        <div className="flex items-center justify-end gap-2 mt-4">
          <Button variant="secondary" size="sm" onClick={apply} disabled={!dirty}>
            Apply changes
          </Button>
          {onSave && (
            <Button variant="primary" size="sm" onClick={() => onSave()} disabled={!dirty || isSaving} icon={<Save className="w-4 h-4" />}>
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Header values support secret references like {'${secret:NAME}'}. These are resolved securely on the server.
        </p>
      </div>
    </Card>
  );
};

export default McpServersCard;
